// /**
//  * Warehouses - list
//  */

// React
import React from 'react';
import { Link } from 'react-router-dom';

// UI
import theme from '../../../theme';
import {
  createStyles,
  makeStyles,
  Theme,
  withStyles,
} from '@material-ui/core/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  IconButton,
  Tooltip,
  Button,
  Grid,
} from '@material-ui/core';

// Icon
import Icon from '@mdi/react';
import {
  mdiChevronDown,
  mdiFileDocumentOutline,
  mdiChevronUp,
  mdiPlusBoxMultiple,
} from '@mdi/js';

// Helper
import {
  translate,
} from '../../../utils/helpers';

//Component
import jMoment from 'moment-jalaali';

// Type
import { Parcels } from '../../parcels/interfaces';
import getComparator from '../../../utils/Table/getComparator';
import stableSort from '../../../utils/Table/stableSort';

type Data = Parcels;
type Props = {
  data: any;
  handleOpenConfirmDisable: (id: number) => void;
  handleOpenConfirmActive: (id: number) => void;
  handleOpen: (id: number, type: string) => void;
  changeStatus: any;
};

type Ord = 'asc' | 'desc';
interface HeadCell {
  // id: keyof Data;
  id: any;
  label: string;
}

interface EnhancedTableProps {
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => void;
  order: Ord;
  orderBy: string;
  rowCount: number;
}

const HtmlTooltip = withStyles((theme: Theme) => ({
  tooltip: {
    backgroundColor: '#f5f5f9',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))(Tooltip);

// Style
const StyledTableCell = withStyles((theme: Theme) =>
  createStyles({
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    body: {
      fontSize: 14,
    },
  })
)(TableCell);

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      '& .MuiTableCell-root': {
        padding: '24px',
      },
    },
    paper: {
      width: '100%',
    },
    columnText: {
      padding: '0 10px',
    },
    table: {
      minWidth: 750,
    },
    head: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
    },
    visuallyHidden: {
      border: 0,
      clip: 'rect(0 0 0 0)',
      height: 1,
      margin: -1,
      overflow: 'hidden',
      padding: 0,
      position: 'absolute',
      top: 20,
      width: 1,
    },
  })
);

const headCells: HeadCell[] = [
  {
    id: 'id',
    label: 'شناسه محموله',
  },
  {
    id: 'partnerName',
    label: 'شرکت',
  },
  {
    id: 'pickAddress',
    label: 'آدرس',
  },
  {
    id: 'pickDate',
    label: 'تاریخ جمع آوری',
  },
  {
    id: 'pickTime',
    label: 'بازه جمع آوری',
  },
  {
    id: 'size',
    label: 'ابعاد',
  },
  {
    id: 'status',
    label: 'وضعیت محموله',
  },
];

const IconSort = (order: Ord) => {
  return (
    <Icon path={order === 'asc' ? mdiChevronDown : mdiChevronUp} size={1} />
  );
};

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort } = props;
  const classes = useStyles();
  const createSortHandler = (property: keyof Data) => (
    event: React.MouseEvent<unknown>
  ) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map(headCell => {
          let orderStatus = orderBy === headCell.id ? order : 'asc';
          return (
            <StyledTableCell
              key={headCell.id}
              align="center"
              sortDirection={orderBy === headCell.id ? order : false}
              data-cy={headCell.id}
            >
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderStatus}
                onClick={createSortHandler(headCell.id)}
                IconComponent={() => IconSort(orderStatus)}
              >
                <span className={classes.columnText}>{headCell.label}</span>
              </TableSortLabel>
            </StyledTableCell>
          );
        })}
        <StyledTableCell />
      </TableRow>
    </TableHead>
  );
}

export default function EnhancedTable(props: Props) {
  const classes = useStyles();
  const [order, setOrder] = React.useState<Ord>('desc');
  const [orderBy, setOrderBy] = React.useState<any>('id');
  const [dense] = React.useState(false);
  const { handleOpenConfirmDisable, handleOpenConfirmActive, handleOpen } = props;

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  return (
    <div className={classes.root}>
      <TableContainer component={Paper}>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size={dense ? 'small' : 'medium'}
          aria-label="enhanced table"
        >
          <EnhancedTableHead
            classes={classes}
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={props.data.length}
          />
          <TableBody>
            {stableSort(props.data, getComparator(order, orderBy))
              .slice()
              .map((row: any, index: number) => {
                const labelId = `enhanced-table-checkbox-${index}`;

                return (
                  <TableRow key={index}>
                    <TableCell
                      component="th"
                      id={labelId}
                      scope="row"
                      align="center"
                    >
                      {row.id}
                    </TableCell>
                    <TableCell align="center">
                      {row.partnerName}
                    </TableCell>
                    <TableCell align="center">
                      <HtmlTooltip
                        title={
                          row?.ronadTransportationDTO?.pickAddress
                        }
                      >
                        <span>
                          {`${row?.ronadTransportationDTO?.pickAddress?.substring(0, 20)}`}
                        </span>
                      </HtmlTooltip>
                    </TableCell>
                    <TableCell align="center">
                      {row?.ronadTransportationDTO?.pickDate && jMoment(row?.ronadTransportationDTO?.pickDate, 'YYYY-MM-DD')
                        .locale('fa')
                        .format('jYYYY/jMM/jDD')}
                    </TableCell>
                    <TableCell align="center">{row?.ronadTransportationDTO?.pickTime}</TableCell>
                    <TableCell align="center">
                      {row.parcelPackages?.length > 1 ? (
                        <HtmlTooltip
                          title={
                            <React.Fragment>
                              {row.parcelPackages.map((itemPackage: any) => {
                                return (
                                  <p style={{ display: "flex", justifyContent: "space-between" }}>
                                    <span className='ml-1'>{itemPackage?.count}</span>
                                    <span className='ml-1'>{`${itemPackage.length}×${itemPackage.height}×${itemPackage.width}`}</span>
                                    <span>{itemPackage?.weight}</span>
                                  </p>
                                );
                              })}
                            </React.Fragment>
                          }
                        >
                          <Button>
                            <Grid
                              style={{ flexWrap: 'nowrap' }}
                              container
                              alignItems="center"
                              spacing={2}
                            >
                              <p style={{ display: "flex", justifyContent: "space-between" }}>
                                <span className='ml-1'>{row.parcelPackages[0].count}</span>
                                <span className='ml-1'>{`${row.parcelPackages[0].length}×${row.parcelPackages[0].height}×${row.parcelPackages[0].width}`}</span>
                                <span>{row.parcelPackages[0].weight}</span>
                              </p>
                              {row.parcelPackages.length > 1 && (
                                <span style={{ padding: '5px' }}>
                                  <Icon
                                    color={theme.palette.green.main}
                                    path={mdiPlusBoxMultiple}
                                    size={1}
                                  />
                                </span>
                              )}
                            </Grid>
                          </Button>
                        </HtmlTooltip>
                      ) : row.parcelPackages?.length === 1 ? (
                        <p style={{ display: "flex", justifyContent: "space-between" }}>
                          <span className='ml-1'>{row.parcelPackages[0].count}</span>
                          <span className='ml-1'>{`${row.parcelPackages[0].length}×${row.parcelPackages[0].height}×${row.parcelPackages[0].width}`}</span>
                          <span>{row.parcelPackages[0].weight}</span>
                        </p>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {translate(row.status, 'parcel')}
                    </TableCell>
                    <TableCell align="right">
                      {/* <Link to={`/pickups/assignment/${row.id}`}>
                        <IconButton size="small" title="تخصیص محموله">
                          <Icon
                            color={theme.palette.orange.main}
                            path={mdiPlusBoxMultipleOutline}
                            size={1}
                          />
                        </IconButton>
                      </Link> */}
                      <Link to={`/pickups/details/${row.id}`}>
                        <IconButton
                          aria-label="details"
                          size="small"
                          title="جزئیات محموله"
                          data-cy="details"
                        >
                          <Icon
                            path={mdiFileDocumentOutline}
                            size={1}
                            color={theme.palette.green.main}
                          />
                        </IconButton>
                      </Link>
                      {/* <Link to={`/parcels/${row.id}/allLogs`}>
                        <IconButton size="small" title="تاریخچه محموله">
                          <Icon path={mdiHistory} size={1} />
                        </IconButton>
                      </Link> */}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
